
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from "react-redux";
import { AES } from 'crypto-js';
import Modal from "react-modal";
import axios from "axios";
import { toast } from "react-toastify";
import "./ClientOverviewListingLatestProject.css";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Link } from 'react-router-dom';
import { useFetchClientProjectDetails } from '../ClientInvoice/useClientInvoice';
import DateConverter from '../../../helpers/dateFormatChange';
import { TrackedLatestProjectHours } from '../../../helpers/ProjectHelpers';
import crssrImage from "../../../images/singleinvoice/cross-red.svg";

export function detailCTA(rowData) {
  const encryptedCliId = AES.encrypt(rowData.client_name._id, 'encryptionKey').toString();
  const encodedId = encodeURIComponent(encryptedCliId).replace(/\//g, '%2F');

  return (
    <Link to={`/client/projects/details/${rowData._id}/${encodedId}`} className="cta-link">
      Details
    </Link>
  );
}

// HubSpot sprocket SVG logo
const HubSpotLogo = ({ size = 18, color = "#ff7a59" }) => (
  <svg width={size} height={size} viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <path d="M426.6 241.3c-18.7-26.5-45.7-43.2-76.2-48.1v-54.7c13.5-5.8 22.9-19.2 22.9-34.8V92.3C373.3 69 354.3 50 331 50h-11.4c-23.3 0-42.3 19-42.3 42.3v11.4c0 15.6 9.4 29 22.9 34.8v54.7c-22.4 3.4-43.5 12.8-61.5 27.7L117.2 141c1-3.7 1.6-7.6 1.6-11.6 0-25.3-20.5-45.8-45.8-45.8S27.2 104.1 27.2 129.4s20.5 45.8 45.8 45.8c10.6 0 20.4-3.6 28.1-9.6l119.1 78.3c-11.2 20.4-17.4 43.7-17.4 67.8 0 28.3 8.4 55.2 23.9 78l-36.2 36.2c-2.8-.9-5.7-1.4-8.8-1.4-16.5 0-29.9 13.4-29.9 29.9S165.2 484 181.7 484s29.9-13.4 29.9-29.9c0-3.1-.5-6.1-1.4-8.8l35.8-35.8c23.3 16.3 51.1 25.5 80.6 25.5 78.7 0 142.6-63.9 142.6-142.6 0-21.2-4.7-41.7-13.4-60.1l-8.2 8.6-.9-1.6zM325.6 392.5c-55.8 0-101.1-45.3-101.1-101.1s45.3-101.1 101.1-101.1 101.1 45.3 101.1 101.1-45.3 101.1-101.1 101.1z" fill={color}/>
  </svg>
);

const LatestProject = (props) => {
  let token = null;
  const { user } = useSelector((state) => state.auth);
  if (user && user.token) token = user.token;

  const { data: project = [], isLoading, refetch: refetchProjects } = useFetchClientProjectDetails(props.id, token);

  // HubSpot state
  const [hubspotConnected, setHubspotConnected] = useState(false);
  const [hubspotCompanyId, setHubspotCompanyId] = useState(null);

  // Sync modal
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [hsProjects, setHsProjects] = useState([]);
  const [hsProjectsLoading, setHsProjectsLoading] = useState(false);

  // Push modal
  const [pushProject, setPushProject] = useState(null); // the HS project being pushed
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [pushForm, setPushForm] = useState({
    project_name: "",
    start_date: "",
    end_date: "",
    budget: "",
    is_billable: true,
  });
  const [needsForm, setNeedsForm] = useState(false); // true if hs_name is missing
  const [editMode, setEditMode] = useState(false);   // true when user clicks Edit in summary view
  const [confirmed, setConfirmed] = useState(false);
  const [pushing, setPushing] = useState(false);

  // Check HubSpot config + client mapping on mount
  useEffect(() => {
    if (!token || !props.id) return;
    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      axios.get("/api/integrations/hubspot/config", { headers }).catch(() => null),
      axios.get(`/api/client-details/${props.id}`, { headers }).catch(() => null),
    ]).then(([configRes, clientRes]) => {
      const connected = configRes?.data?.connected === true;
      setHubspotConnected(connected);

      const companyId = clientRes?.data?.hubspot_company_id || null;
      setHubspotCompanyId(companyId);
    });
  }, [token, props.id]);

  const openSyncModal = useCallback(async () => {
    if (!hubspotCompanyId) return;
    setSyncModalOpen(true);
    setHsProjectsLoading(true);
    try {
      const res = await axios.get(
        `/api/integrations/hubspot/company/${hubspotCompanyId}/projects`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHsProjects(res.data.projects || []);
    } catch (err) {
      toast.error("Failed to load HubSpot projects.", { position: "bottom-right", autoClose: 2000 });
    } finally {
      setHsProjectsLoading(false);
    }
  }, [hubspotCompanyId, token]);

  const openPushModal = (hsProject) => {
    const missing = !hsProject.name;
    setPushProject(hsProject);
    setNeedsForm(missing);
    setEditMode(false);
    setConfirmed(false);
    setPushForm({
      project_name: hsProject.name || "",
      start_date: hsProject.start_date ? hsProject.start_date.slice(0, 10) : "",
      end_date: hsProject.end_date ? hsProject.end_date.slice(0, 10) : "",
      budget: "",
      is_billable: true,
    });
    setPushModalOpen(true);
  };

  const handlePushSubmit = async () => {
    if (!confirmed && !needsForm) {
      toast.warn("Please confirm before pushing.", { position: "bottom-right", autoClose: 2000 });
      return;
    }
    setPushing(true);
    try {
      const res = await axios.post(
        `/api/integrations/hubspot/projects/${pushProject.id}/push`,
        {
          clientId: props.id,
          project_name: pushForm.project_name,
          start_date: pushForm.start_date || null,
          end_date: pushForm.end_date || null,
          budget: pushForm.budget || 0,
          is_billable: pushForm.is_billable,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.status === "already_synced") {
        toast.info("This project is already synced to Talenton.", { position: "bottom-right", autoClose: 3000 });
      } else {
        toast.success(`Project "${pushForm.project_name}" pushed to Talenton!`, { position: "bottom-right", autoClose: 3000 });
        // Refresh HS projects list to show updated sync status
        setHsProjects(prev =>
          prev.map(p => p.id === pushProject.id
            ? { ...p, synced: true, talenton_project_name: pushForm.project_name }
            : p
          )
        );
        refetchProjects();
      }
      setPushModalOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to push project.", { position: "bottom-right", autoClose: 3000 });
    } finally {
      setPushing(false);
    }
  };

  const renderProjectStatus = (rowData) => {
    const statusClass = rowData.is_billable === true ? "billable" : "non-billable";
    const statusName = rowData.is_billable === true ? "Yes" : "No";
    return <span className={`payment-status ${statusClass}`}>{statusName}</span>;
  };

  const showSyncButton = hubspotConnected && hubspotCompanyId;

  const syncModalContentStyle = {
    content: {
      width: "480px",
      position: "fixed",
      top: "50%",
      left: "50%",
      parent: document.querySelector(".admin-outer.time.tracker"),
    },
  };

  const pushModalContentStyle = {
    content: {
      width: "34%",
      position: "fixed",
      top: "50%",
      left: "50%",
      parent: document.querySelector(".admin-outer.time.tracker"),
    },
  };

  return (
    <div className="latest-project-outer">
      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }

        /* Sync trigger button */
        .hs-sync-btn {
          display: flex; align-items: center; gap: 6px;
          background: none; border: 1.5px solid #e8e8e8;
          border-radius: 8px; padding: 5px 12px;
          font-size: 13px; font-weight: 500; color: #454F59;
          cursor: pointer; transition: all 0.18s; line-height: 1.4;
        }
        .hs-sync-btn:hover { border-color: #ff7a59; color: #ff7a59; background: #fff5f2; }

        /* Scrollable project list */
        .hs-project-scroll {
          max-height: 50vh; overflow-y: auto;
        }
        .hs-project-scroll::-webkit-scrollbar { width: 4px; }
        .hs-project-scroll::-webkit-scrollbar-track { background: transparent; }
        .hs-project-scroll::-webkit-scrollbar-thumb { background: #e0e0e0; border-radius: 4px; }

        /* Project cards */
        .hs-project-list { display: flex; flex-direction: column; gap: 10px; }
        .hs-project-item {
          display: flex; align-items: center; justify-content: space-between;
          gap: 12px;
          padding: 14px 16px; border-radius: 10px;
          border: 1px solid #eef0f2; background: #fff;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .hs-project-item:hover { border-color: #d6dbff; box-shadow: 0 2px 8px rgba(100,121,248,0.07); }
        .hs-project-item-info { flex: 1; min-width: 0; }
        .hs-project-item-name {
          font-size: 14px; font-weight: 600; color: #282828;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          line-height: 1.4;
        }
        .hs-project-item-meta {
          font-size: 12px; color: #9ca3af; margin-top: 3px; line-height: 1.4;
        }
        .hs-project-item-synced-name {
          font-size: 12px; color: #16a34a; margin-top: 2px;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }

        /* Right-side action area — fixed width so items align */
        .hs-project-item-action { flex-shrink: 0; display: flex; justify-content: flex-end; }

        /* Synced badge */
        .hs-synced-badge {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 12px; font-weight: 600; color: #16a34a;
          background: #dcfce7; border: 1px solid #bbf7d0;
          border-radius: 20px; padding: 4px 11px;
          white-space: nowrap;
        }

        /* Push button */
        .hs-push-btn {
          background: #6479F8; color: #fff; border: none; border-radius: 6px;
          padding: 5px 10px; font-size: 11px; font-weight: 600;
          cursor: pointer; white-space: nowrap;
          transition: background 0.15s; letter-spacing: 0.2px;
        }
        .hs-push-btn:hover { background: #4f63d4; }

        /* Push modal form */
        .hs-form-label { font-size: 13px; font-weight: 600; color: #454F59; margin-bottom: 5px; display: block; }
        .hs-form-input {
          width: 100%; padding: 8px 12px; border: 1px solid #e0e0e0; border-radius: 8px;
          font-size: 14px; color: #282828; box-sizing: border-box; outline: none;
          transition: border-color 0.15s;
        }
        .hs-form-input:focus { border-color: #6479F8; }
        .hs-form-row { margin-bottom: 14px; }
        .hs-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .hs-confirm-row {
          display: flex; align-items: flex-start; gap: 10px; margin: 18px 0 0;
          padding: 14px; background: #f7f8ff; border-radius: 8px;
          border: 1px solid #e0e5ff;
        }
        .hs-confirm-row input[type=checkbox] { margin-top: 2px; width: 16px; height: 16px; cursor: pointer; flex-shrink: 0; accent-color: #6479F8; }
        .hs-confirm-row label { font-size: 13px; color: #454F59; cursor: pointer; line-height: 1.5; }
        .hs-detail-card {
          background: #f7f8ff; border-radius: 10px; padding: 14px 16px;
          margin-bottom: 16px; display: flex; gap: 12px; align-items: center;
          border: 1px solid #e0e5ff;
        }
        .hs-detail-avatar {
          width: 40px; height: 40px; border-radius: 50%; background: #6479F8;
          color: #fff; display: flex; align-items: center; justify-content: center;
          font-size: 18px; font-weight: 700; flex-shrink: 0;
        }
        .hs-detail-rows { border-radius: 10px; border: 1px solid #ebebeb; overflow: hidden; }
        .hs-detail-row { display: flex; padding: 9px 14px; font-size: 13px; border-bottom: 1px solid #f3f4f6; }
        .hs-detail-row:last-child { border-bottom: none; }
        .hs-detail-row:nth-child(even) { background: #fafbff; }
        .hs-detail-row-label { color: #9ca3af; width: 110px; flex-shrink: 0; font-weight: 500; }
        .hs-detail-row-value { color: #282828; font-weight: 500; }
        .hs-modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
        .hs-cancel-btn {
          background: none; border: 1.5px solid #e0e0e0; border-radius: 8px;
          padding: 8px 18px; font-size: 14px; color: #454F59; cursor: pointer;
          transition: border-color 0.15s;
        }
        .hs-cancel-btn:hover { border-color: #aaa; }
        .hs-submit-btn {
          background: #6479F8; color: #fff; border: none; border-radius: 8px;
          padding: 8px 22px; font-size: 14px; font-weight: 600; cursor: pointer;
          transition: background 0.15s;
        }
        .hs-submit-btn:hover:not(:disabled) { background: #4f63d4; }
        .hs-submit-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .hs-empty { text-align: center; color: #c0c4cc; padding: 36px 0; font-size: 14px; }
        .hs-loading { text-align: center; color: #c0c4cc; padding: 36px 0; font-size: 14px; }
        .hs-missing-notice {
          font-size: 12px; color: #e57373; background: #fff5f5; border: 1px solid #fecaca;
          border-radius: 7px; padding: 8px 12px; margin-bottom: 14px;
        }
      `}</style>

      <div className="table-head-outer">
        <div className="table-head">Latest Projects</div>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          {showSyncButton && (
            <button className="hs-sync-btn" onClick={openSyncModal}>
              <HubSpotLogo size={16} color="#ff7a59" />
              Sync from HubSpot
            </button>
          )}
          <div className="view-button">
            <Link
              to={`/client/projects/${encodeURIComponent(
                AES.encrypt(props.id, 'encryptionKey').toString().replace(/\//g, '%2F')
              )}`}
              className={window.location.pathname.startsWith('/client/projects') ? 'active' : ''}
            >
              View All
            </Link>
          </div>
        </div>
      </div>

      {isLoading ? (
        <DataTable
          value={Array.from({ length: 5 })}
          removableSort
          tableStyle={{ minWidth: "50rem", width: "100%", textAlign: "left" }}
          paginator={false}
          rows={5}
        >
          <Column header="Project" sortable style={{ width: "18.7%" }} body={() => <div style={{ width: "180px", height: "22px", borderRadius: "4px", backgroundColor: "#e5e7eb", animation: "pulse 1.5s ease-in-out infinite" }} />} />
          <Column header="Date Created" sortable style={{ width: "15.3%" }} body={() => <div style={{ width: "130px", height: "22px", borderRadius: "4px", backgroundColor: "#e5e7eb", animation: "pulse 1.5s ease-in-out infinite" }} />} />
          <Column header="Tracked Hours" sortable style={{ width: "14.7%" }} body={() => <div style={{ width: "90px", height: "22px", borderRadius: "4px", backgroundColor: "#e5e7eb", animation: "pulse 1.5s ease-in-out infinite" }} />} />
          <Column header="Billable" sortable style={{ width: "10.4%" }} body={() => <div style={{ width: "70px", height: "30px", borderRadius: "100px", backgroundColor: "#e5e7eb", animation: "pulse 1.5s ease-in-out infinite" }} />} />
          <Column field="_id" style={{ width: "15%" }} body={() => <div style={{ width: "86px", height: "30px", borderRadius: "8px", backgroundColor: "#e5e7eb", animation: "pulse 1.5s ease-in-out infinite" }} />} />
        </DataTable>
      ) : (
        <DataTable
          value={project.slice(0, 5)}
          removableSort
          tableStyle={{ minWidth: "50rem", width: "100%", textAlign: "left" }}
          paginator={false}
          rows={5}
          emptyMessage="No Projects found under this client"
        >
          <Column field="project_name" header="Project" sortable style={{ width: "18.7%" }} />
          <Column field="created_at" body={(rowData) => <DateConverter date={rowData.created_at} />} header="Date Created" sortable style={{ width: "15.3%" }} />
          <Column field="tracked_hours" header="Tracked Hours" body={TrackedLatestProjectHours} sortable style={{ width: "14.7%" }} />
          <Column field="is_billable" header="Billable" body={renderProjectStatus} sortable style={{ width: "10.4%" }} />
          <Column field="_id" body={detailCTA} style={{ width: "15%" }} />
        </DataTable>
      )}

      {/* ── HubSpot Sync Modal ── */}
      <Modal
        className="client-modal"
        isOpen={syncModalOpen}
        onRequestClose={() => setSyncModalOpen(false)}
        contentLabel="HubSpot Projects"
        style={syncModalContentStyle}
      >
        <div className="modal">
          <div className="modal-close-invite outter-modal-main">
            <a onClick={() => setSyncModalOpen(false)} style={{ cursor: "pointer" }}>
              <img src={crssrImage} alt="Close" />
            </a>
          </div>
          <div className="modal-client-head">
            <h2 style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <HubSpotLogo size={20} color="#ff7a59" />
              HubSpot Projects
            </h2>
          </div>
          <div className="form-field-client-outer">
            <div className="hs-project-scroll">
              {hsProjectsLoading ? (
                <div className="hs-loading">Loading projects from HubSpot…</div>
              ) : hsProjects.length === 0 ? (
                <div className="hs-empty">No projects found under this HubSpot company.</div>
              ) : (
                <div className="hs-project-list">
                  {hsProjects.map((proj) => (
                    <div key={proj.id} className="hs-project-item" title={proj.name || ""}>
                      <div className="hs-project-item-info">
                        <div className="hs-project-item-name">
                          {proj.name || <em style={{ color: "#c0c4cc", fontWeight: 400 }}>Unnamed project</em>}
                        </div>
                        <div className="hs-project-item-meta">
                          {[
                            proj.start_date && `Start: ${proj.start_date.slice(0, 10)}`,
                            proj.end_date && `Due: ${proj.end_date.slice(0, 10)}`,
                          ].filter(Boolean).join("  ·  ") || "No dates set"}
                        </div>
                        {proj.synced && proj.talenton_project_name && (
                          <div className="hs-project-item-synced-name" title={proj.talenton_project_name}>
                            ✓ {proj.talenton_project_name}
                          </div>
                        )}
                      </div>
                      <div className="hs-project-item-action">
                        {proj.synced ? (
                          <span className="hs-synced-badge">✓ Synced</span>
                        ) : (
                          <button className="hs-push-btn" onClick={() => { setSyncModalOpen(false); openPushModal(proj); }}>
                            Push to Talenton
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* ── Push to Talenton Modal ── */}
      <Modal
        className="client-modal"
        isOpen={pushModalOpen}
        onRequestClose={() => setPushModalOpen(false)}
        contentLabel="Push to Talenton"
        style={pushModalContentStyle}
      >
        <div className="modal">
          <div className="modal-close-invite outter-modal-main">
            <a onClick={() => setPushModalOpen(false)} style={{ cursor: "pointer" }}>
              <img src={crssrImage} alt="Close" />
            </a>
          </div>
          <div className="modal-client-head">
            <h2 style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <HubSpotLogo size={20} color="#ff7a59" />
              Push to Talenton
            </h2>
          </div>
          <div className="form-field-client-outer">
            {/* Project source card */}
            <div className="hs-detail-card">
              <div className="hs-detail-avatar">
                {(pushProject?.name || "?")[0]?.toUpperCase()}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#282828", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {pushProject?.name || "Unnamed project"}
                </div>
                <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>HubSpot ID: {pushProject?.id}</div>
              </div>
            </div>

            {(needsForm || editMode) ? (
              // ── Editable form ──
              <>
                {needsForm && (
                  <div className="hs-missing-notice">
                    Project name is missing in HubSpot. Please fill in the details below.
                  </div>
                )}
                <div className="hs-form-row">
                  <label className="hs-form-label">Project Name <span style={{ color: "#e57373" }}>*</span></label>
                  <input
                    className="hs-form-input"
                    value={pushForm.project_name}
                    onChange={e => setPushForm(f => ({ ...f, project_name: e.target.value }))}
                    placeholder="Enter project name"
                    autoFocus={needsForm}
                  />
                </div>
                <div className="hs-form-grid">
                  <div className="hs-form-row">
                    <label className="hs-form-label">Start Date</label>
                    <input className="hs-form-input" type="date" value={pushForm.start_date} onChange={e => setPushForm(f => ({ ...f, start_date: e.target.value }))} />
                  </div>
                  <div className="hs-form-row">
                    <label className="hs-form-label">End Date</label>
                    <input className="hs-form-input" type="date" value={pushForm.end_date} onChange={e => setPushForm(f => ({ ...f, end_date: e.target.value }))} />
                  </div>
                </div>
                <div className="hs-form-grid">
                  <div className="hs-form-row">
                    <label className="hs-form-label">Budget</label>
                    <input className="hs-form-input" type="number" min="0" value={pushForm.budget} onChange={e => setPushForm(f => ({ ...f, budget: e.target.value }))} placeholder="0" />
                  </div>
                  <div className="hs-form-row">
                    <label className="hs-form-label">Billable</label>
                    <select className="hs-form-input" value={pushForm.is_billable ? "yes" : "no"} onChange={e => setPushForm(f => ({ ...f, is_billable: e.target.value === "yes" }))}>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>
                {editMode && (
                  <div className="hs-confirm-row">
                    <input type="checkbox" id="hs-confirm" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
                    <label htmlFor="hs-confirm">
                      I confirm this project should be created in Talenton and linked to this HubSpot project.
                    </label>
                  </div>
                )}
              </>
            ) : (
              // ── Summary view ──
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                  <span style={{ fontSize: "12px", color: "#9ca3af", fontWeight: 500 }}>Project details</span>
                  <button
                    onClick={() => { setEditMode(true); setConfirmed(false); }}
                    style={{
                      background: "none", border: "1px solid #e0e0e0", borderRadius: "6px",
                      padding: "3px 10px", fontSize: "12px", color: "#6479F8",
                      cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px",
                    }}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                    Edit
                  </button>
                </div>
                <div className="hs-detail-rows">
                  {[
                    { label: "Project Name", value: pushForm.project_name },
                    { label: "Start Date", value: pushForm.start_date || "—" },
                    { label: "End Date", value: pushForm.end_date || "—" },
                    { label: "Budget", value: pushForm.budget ? `$${pushForm.budget}` : "—" },
                    { label: "Billable", value: pushForm.is_billable ? "Yes" : "No" },
                  ].map((row) => (
                    <div key={row.label} className="hs-detail-row">
                      <span className="hs-detail-row-label">{row.label}</span>
                      <span className="hs-detail-row-value" style={{ color: row.value === "—" ? "#c0c4cc" : "#282828" }}>{row.value}</span>
                    </div>
                  ))}
                </div>
                <div className="hs-confirm-row">
                  <input type="checkbox" id="hs-confirm" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
                  <label htmlFor="hs-confirm">
                    I confirm this project should be created in Talenton and linked to this HubSpot project.
                  </label>
                </div>
              </>
            )}

            <div className="hs-modal-actions">
              <button className="hs-cancel-btn" onClick={() => setPushModalOpen(false)}>Cancel</button>
              <button
                className="hs-submit-btn"
                disabled={pushing || (!pushForm.project_name.trim()) || ((!needsForm && !editMode) && !confirmed) || (editMode && !confirmed)}
                onClick={handlePushSubmit}
              >
                {pushing ? "Pushing…" : "Push to Talenton"}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LatestProject;
